import { useMemo } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';

const TimeItemWrapper = styled('div')({
    display: 'grid',
    gridTemplateColumns: '64px max-content',
    alignItems: 'center',
    gridGap: '8px',
});

const AutoCloseInput = ({ isView, fieldName, base }) => {
    const { t } = useTranslation();
    const { control, setValue, trigger } = useFormContext();
    const { errors } = useFormState({ control });

    const minutesOptions = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                label: (i * 5).toString(),
                value: (i * 5).toString().padStart(2, '0'),
            })),
        [],
    );
    const hoursOptions = useMemo(
        () => Array.from({ length: 24 }, (_, i) => ({ label: i.toString(), value: i.toString().padStart(2, '0') })),
        [],
    );
    const daysOptions = useMemo(
        () => Array.from({ length: 31 }, (_, i) => ({ label: i.toString(), value: i.toString().padStart(2, '0') })),
        [],
    );

    return (
        <>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', margin: '16px 0 24px' }}>
                {t('Agents.timeOfClientInactivity')}
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                    { name: `days${base}`, label: t('common.day(s)'), options: daysOptions },
                    { name: `hours${base}`, label: t('common.hour(s)'), options: hoursOptions },
                    { name: `minutes${base}`, label: t('common.minute(s)'), options: minutesOptions },
                ].map(({ name, label, options }) => (
                    <TimeItemWrapper key={name}>
                        <Controller
                            control={control}
                            name={name}
                            render={({ field, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue(name, value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        trigger(fieldName);
                                    }}
                                    onBlur={() => trigger(fieldName)}
                                    options={options}
                                    label={''}
                                    fullWidth
                                    required
                                    error={!!errors[fieldName]}
                                    disabled={isView}
                                />
                            )}
                        />
                        <Typography
                            variant="body2"
                            sx={{ color: 'var(--text-primary)', transform: 'translateY(-8px)' }}
                        >
                            {label}
                        </Typography>
                    </TimeItemWrapper>
                ))}
            </div>
            <Typography
                variant="caption"
                sx={{
                    transform: 'translateY(-11px)',
                    display: 'block',
                    margin: '0 12px',
                    fontFamily: 'Rubik, sans-serif',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '14px',
                    letterSpacing: '0.4px',
                    color: 'var(--error-main)',
                }}
            >
                {t(errors[fieldName]?.message)}
            </Typography>
        </>
    );
};

export default AutoCloseInput;
